// ============================================
// APPLICATION - Ecommerce Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ecommerceRepository } from '../../infrastructure/http/ecommerce.repository';
import { queryKeys } from '../query-keys';
import type { PedidosFiltros } from '../../domain/dtos';

/**
 * Hook para obtener pedidos del ecommerce
 */
export const usePedidos = (filtros?: PedidosFiltros, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...queryKeys.ecommerce.pedidos, filtros],
    queryFn: () => ecommerceRepository.obtenerPedidos(filtros),
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 1 * 60 * 1000, // 1 minuto
    retry: 1,
    throwOnError: false,
    enabled, // Solo ejecuta la query si enabled es true
  });
};

/**
 * Hook de polling para contar pedidos ecommerce pendientes.
 * Refresca cada 2 minutos solo cuando la pestaña está activa.
 */
export const usePedidosPendientesCount = () => {
  return useQuery({
    queryKey: queryKeys.ecommerce.pendientesCount,
    queryFn: () => ecommerceRepository.obtenerPedidos({ estado: 'PENDIENTE', page: 0 }),
    select: (data) => data.totalElementos,
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
    staleTime: 90 * 1000,
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para cancelar un pedido
 */
export const useCancelarPedido = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pedidoId: number) => ecommerceRepository.cancelarPedido(pedidoId),
    onSuccess: () => {
      // Invalida todas las vistas de pedidos (independiente de filtros activos)
      // y el contador del badge para que se sincronicen en el siguiente refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.ecommerce.pedidos });
      queryClient.invalidateQueries({ queryKey: queryKeys.ecommerce.pendientesCount });
    },
  });
};

/**
 * Hook para completar un pedido
 */
export const useCompletarPedido = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pedidoId: number) => ecommerceRepository.completarPedido(pedidoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ecommerce.pedidos });
      queryClient.invalidateQueries({ queryKey: queryKeys.ecommerce.pendientesCount });
    },
  });
};
